import React, { useState, useEffect } from 'react';
import { Card, Avatar, Typography, Skeleton, Popover } from 'antd';
import { Crown, Flame } from 'lucide-react';
import { motion } from 'framer-motion';
import { getLeaderboard, getDashboardStats } from '../services/goalsPerformanceApi';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useGoalsSettings } from '../contexts/GoalsSettingsContext';
import { resolveUserAvatar } from '../utils/userAvatar';

const { Text } = Typography;

// Rank badge colors with subtle premium feel
const rankColors = {
  1: { bg: 'bg-yellow-400 dark:bg-yellow-500 border border-yellow-200 dark:border-yellow-600', text: 'text-yellow-900 dark:text-zinc-900' },
  2: { bg: 'bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600', text: 'text-slate-700 dark:text-slate-300' },
  3: { bg: 'bg-orange-100 dark:bg-orange-900 border border-orange-300 dark:border-orange-700', text: 'text-orange-700 dark:text-orange-400' },
  4: { bg: 'bg-blue-100 dark:bg-blue-900 border border-blue-200 dark:border-blue-700', text: 'text-blue-700 dark:text-blue-300' },
  5: { bg: 'bg-indigo-100 dark:bg-indigo-900 border border-indigo-200 dark:border-indigo-700', text: 'text-indigo-700 dark:text-indigo-300' }
};

// Avatar colors for different ranks matching the subtle theme
const avatarColors = {
  1: 'bg-gradient-to-tr from-yellow-500 to-yellow-400',
  2: 'bg-gradient-to-tr from-slate-500 to-slate-400',
  3: 'bg-gradient-to-tr from-orange-500 to-orange-400',
  4: 'bg-gradient-to-tr from-blue-500 to-blue-400',
  5: 'bg-gradient-to-tr from-indigo-500 to-indigo-400'
};

// Get initials from name
const getInitials = (firstName, lastName) => {
  const first = firstName ? firstName.charAt(0).toUpperCase() : '';
  const last = lastName ? lastName.charAt(0).toUpperCase() : '';
  return `${first}${last}` || 'U';
};

const GoalsLeaderboardCard = () => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const { user } = useAuth();
  const { settings } = useGoalsSettings();
  const badgeRanges = settings?.badgeRanges || [];
  const streakIconUrl = settings?.streakPoints?.icon?.url || null;
  const streakMonths = settings?.streakPoints?.months ?? 1;
  const streakPointsPerCycle = settings?.streakPoints?.points ?? 1;
  const [top5, setTop5] = useState([]);
  const [currentUserEntry, setCurrentUserEntry] = useState(null);
  const [userBadge, setUserBadge] = useState(null);
  const [userStreak, setUserStreak] = useState(null);
  const [top5Badges, setTop5Badges] = useState({}); // { userId: { badge, streak } }
  const [loading, setLoading] = useState(true);

  // Get badge icon URL from settings based on badge name
  const getBadgeIconUrl = (badgeName) => {
    if (!badgeName || !badgeRanges || badgeRanges.length === 0) {
      return null;
    }

    // Try exact match first (case insensitive)
    let badgeRange = badgeRanges.find(range =>
      range.label.toLowerCase() === badgeName.toLowerCase()
    );

    // If no exact match, try partial match
    if (!badgeRange && badgeName) {
      badgeRange = badgeRanges.find(range =>
        badgeName.toLowerCase().includes(range.label.toLowerCase()) ||
        range.label.toLowerCase().includes(badgeName.toLowerCase())
      );
    }

    // Return icon URL if available
    const iconUrl = badgeRange?.icon?.url;
    if (iconUrl && typeof iconUrl === 'string' && iconUrl.trim() !== '') {
      return iconUrl;
    }

    return null;
  };

  // Find the badge range record for a badge name (used for popover details)
  const getBadgeRange = (badgeName) => {
    if (!badgeName || !badgeRanges || badgeRanges.length === 0) return null;
    let badgeRange = badgeRanges.find(r => r.label.toLowerCase() === badgeName.toLowerCase());
    if (!badgeRange) {
      badgeRange = badgeRanges.find(r =>
        badgeName.toLowerCase().includes(r.label.toLowerCase()) ||
        r.label.toLowerCase().includes(badgeName.toLowerCase())
      );
    }
    return badgeRange || null;
  };

  // Popover content for the streak flame
  const renderStreakPopover = (streakCount) => {
    const monthLabel = streakMonths === 1 ? 'month' : `${streakMonths} months`;
    const pointLabel = streakPointsPerCycle === 1 ? 'pt' : 'pts';
    return (
      <div className="max-w-[220px]">
        <div className={`text-sm font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-800'}`}>
          {streakCount} {streakCount === 1 ? 'cycle' : 'cycles'} streak
        </div>
        <div className={`text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
          Earned by hitting goals every {monthLabel}. Worth {streakPointsPerCycle} {pointLabel} per streak cycle.
        </div>
      </div>
    );
  };

  // Popover content for a badge icon
  const renderBadgePopover = (badgeName) => {
    const range = getBadgeRange(badgeName);
    const label = range?.label || badgeName;
    let scoreLine = null;
    if (range) {
      scoreLine = range.lessThan
        ? `Score below ${range.max}`
        : `Score ${range.min} – ${range.max}`;
    }
    return (
      <div className="max-w-[220px]">
        <div className={`text-sm font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-800'}`}>
          {label}
        </div>
        {scoreLine && (
          <div className={`text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
            {scoreLine}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);

        // Fetch top 5
        const top5Response = await getLeaderboard(5);
        console.log('📊 Leaderboard Top 5 Response:', top5Response);

        if (top5Response.success) {
          const top5Data = top5Response.data || [];
          console.log('📊 Top 5 Data:', top5Data);
          setTop5(top5Data);

          // Fetch badge and streak for all top 5 users
          const badgesData = {};
          await Promise.all(
            top5Data.map(async (member) => {
              try {
                const memberUserId = member.userId?.toString() || member.userId?._id?.toString() || member.userId;
                const dashboardResponse = await getDashboardStats(memberUserId);
                if (dashboardResponse.success && dashboardResponse.data) {
                  badgesData[memberUserId] = {
                    badge: dashboardResponse.data.badge || null,
                    streak: dashboardResponse.data.streak || 0
                  };
                }
              } catch (error) {
                console.error(`Error fetching stats for user ${member.userId}:`, error);
              }
            })
          );
          setTop5Badges(badgesData);

          // Check if logged-in user is in top 5
          const currentUserId = user?.id || user?._id;
          console.log('👤 Current User ID:', currentUserId, 'Type:', typeof currentUserId);
          console.log('👤 User Object:', user);

          if (currentUserId) {
            // Normalize userId for comparison
            const normalizedCurrentUserId = currentUserId.toString();
            console.log('👤 Normalized Current User ID:', normalizedCurrentUserId);

            // Check top 5 with multiple comparison methods
            const userInTop5 = top5Data.find(member => {
              const memberUserId = member.userId?.toString() || member.userId?._id?.toString() || member.userId;
              console.log('🔍 Comparing:', {
                memberUserId,
                normalizedCurrentUserId,
                match: memberUserId === normalizedCurrentUserId
              });
              return memberUserId === normalizedCurrentUserId;
            });
            console.log('🔍 User in Top 5?', !!userInTop5, userInTop5);

            // If user is not in top 5, fetch full leaderboard to find user's rank
            if (!userInTop5) {
              try {
                console.log('🔍 Fetching full leaderboard to find user rank...');
                const fullResponse = await getLeaderboard(1000); // Fetch large number to get all users
                if (fullResponse.success) {
                  const fullLeaderboard = fullResponse.data || [];
                  console.log('📊 Full Leaderboard:', fullLeaderboard.length, 'users');

                  const userEntry = fullLeaderboard.find(member => {
                    const memberUserId = member.userId?.toString() || member.userId?._id?.toString() || member.userId;
                    return memberUserId === normalizedCurrentUserId;
                  });
                  console.log('👤 User Entry Found:', userEntry);

                  if (userEntry) {
                    setCurrentUserEntry(userEntry);
                  } else {
                    // User not found in leaderboard - create entry manually (no approved goals or score = 0)
                    console.log('⚠️ User not found in leaderboard - creating entry with 0.0 score');
                    // Calculate rank: if there are users with scores, user's rank is after them
                    const maxRank = fullLeaderboard.length > 0
                      ? Math.max(...fullLeaderboard.map(m => m.rank || 0))
                      : 0;
                    const userEntryManual = {
                      userId: currentUserId,
                      firstName: user?.firstName || 'User',
                      lastName: user?.lastName || '',
                      position: user?.position || 'Team Member',
                      averageScore: 0.0,
                      rank: maxRank + 1, // Rank after all users with scores
                      cyclesCount: 0
                    };
                    console.log('👤 Created Manual User Entry:', userEntryManual);
                    setCurrentUserEntry(userEntryManual);
                  }

                  // Fetch dashboard stats for badge and streak
                  try {
                    const dashboardResponse = await getDashboardStats(currentUserId);
                    if (dashboardResponse.success && dashboardResponse.data) {
                      setUserBadge(dashboardResponse.data.badge || null);
                      setUserStreak(dashboardResponse.data.streak || 0);
                    }
                  } catch (dashboardError) {
                    console.error('Error fetching dashboard stats:', dashboardError);
                  }
                } else {
                  // If full leaderboard fetch fails, still create manual entry
                  console.log('⚠️ Full leaderboard fetch failed, creating manual entry');
                  const userEntryManual = {
                    userId: currentUserId,
                    firstName: user?.firstName || 'User',
                    lastName: user?.lastName || '',
                    position: user?.position || 'Team Member',
                    averageScore: 0.0,
                    rank: top5Data.length + 1, // Rank after top 5
                    cyclesCount: 0
                  };
                  setCurrentUserEntry(userEntryManual);

                  // Fetch dashboard stats for badge and streak
                  try {
                    const dashboardResponse = await getDashboardStats(currentUserId);
                    if (dashboardResponse.success && dashboardResponse.data) {
                      setUserBadge(dashboardResponse.data.badge || null);
                      setUserStreak(dashboardResponse.data.streak || 0);
                    }
                  } catch (dashboardError) {
                    console.error('Error fetching dashboard stats:', dashboardError);
                  }
                }
              } catch (fullError) {
                console.error('Error fetching full leaderboard:', fullError);
                // Even if fetch fails, create manual entry
                const userEntryManual = {
                  userId: currentUserId,
                  firstName: user?.firstName || 'User',
                  lastName: user?.lastName || '',
                  position: user?.position || 'Team Member',
                  averageScore: 0.0,
                  rank: top5Data.length + 1,
                  cyclesCount: 0
                };
                setCurrentUserEntry(userEntryManual);

                // Fetch dashboard stats for badge and streak
                try {
                  const dashboardResponse = await getDashboardStats(currentUserId);
                  if (dashboardResponse.success && dashboardResponse.data) {
                    setUserBadge(dashboardResponse.data.badge || null);
                    setUserStreak(dashboardResponse.data.streak || 0);
                  }
                } catch (dashboardError) {
                  console.error('Error fetching dashboard stats:', dashboardError);
                }
              }
            } else {
              // User is in top 5, still fetch dashboard stats for badge and streak
              try {
                const dashboardResponse = await getDashboardStats(currentUserId);
                if (dashboardResponse.success && dashboardResponse.data) {
                  setUserBadge(dashboardResponse.data.badge || null);
                  setUserStreak(dashboardResponse.data.streak || 0);
                }
              } catch (dashboardError) {
                console.error('Error fetching dashboard stats:', dashboardError);
              }
            }
          } else {
            console.log('⚠️ No current user ID available');
          }
        } else {
          console.error('❌ Leaderboard API failed:', top5Response);
        }
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };

    // Fetch leaderboard regardless of user (top 5 should always show)
    fetchLeaderboard();
  }, [user]);

  if (loading) {
    return (
      <Card
        className={`h-full ${isDark
          ? 'bg-zinc-900/40 border-zinc-800/60'
          : 'bg-white border-slate-200'
          }`}
      >
        <Skeleton active paragraph={{ rows: 5 }} />
      </Card>
    );
  }

  if (top5.length === 0 && !currentUserEntry) {
    return null;
  }

  // Determine what to display
  const displayList = [...top5];
  const showSeparator = currentUserEntry !== null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        className={`h-full ${isDark
          ? 'bg-zinc-900/40 border-zinc-800/60'
          : 'bg-white border-slate-200'
          }`}
        bodyStyle={{ padding: '20px' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100 dark:border-zinc-800/80">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg flex items-center justify-center ${isDark ? 'bg-zinc-800 text-yellow-500' : 'bg-yellow-50 text-yellow-600'}`}>
              <Crown className="h-5 w-5" />
            </div>
            <div>
              <Text
                className={`text-base font-semibold block leading-tight ${isDark ? 'text-white' : 'text-slate-800'
                  }`}
              >
                Goals Leaderboard
              </Text>
              <Text className={`text-xs font-medium ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
                Top performers by score
              </Text>
            </div>
          </div>
          <div
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${isDark
              ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
              : 'bg-blue-50 text-blue-600 border border-blue-100'
              }`}
          >
            Top 5
          </div>
        </div>

        {/* Leaderboard List */}
        <div className="space-y-1.5 mt-2">
          {/* Top 5 */}
          {top5.map((member, index) => {
            const rank = member.rank || index + 1;
            const rankColor = rankColors[rank] || rankColors[5];
            const avatarColor = avatarColors[rank] || avatarColors[5];
            const initials = getInitials(member.firstName, member.lastName);
            const fullName = `${member.firstName || ''} ${member.lastName || ''}`.trim();
            const avatarSrc = resolveUserAvatar(member);

            return (
              <motion.div
                key={member.userId || index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08, duration: 0.3 }}
                className={`flex items-center gap-3 p-2 -mx-2 rounded-lg transition-all duration-200 group relative overflow-hidden ${rank === 1 ? (isDark ? 'bg-yellow-500/5 hover:bg-yellow-500/10' : 'bg-yellow-50/50 hover:bg-yellow-100/50') :
                  rank === 2 ? (isDark ? 'bg-slate-500/5 hover:bg-slate-500/10' : 'bg-slate-50/50 hover:bg-slate-100/50') :
                    rank === 3 ? (isDark ? 'bg-orange-500/5 hover:bg-orange-500/10' : 'bg-orange-50/50 hover:bg-orange-100/50') :
                      rank === 4 ? (isDark ? 'bg-blue-500/5 hover:bg-blue-500/10' : 'bg-blue-50/50 hover:bg-blue-100/50') :
                        (isDark ? 'bg-indigo-500/5 hover:bg-indigo-500/10' : 'bg-indigo-50/50 hover:bg-indigo-100/50')
                  }`}
              >
                {/* Avatar with overlapping Rank Badge */}
                <div className="relative flex-shrink-0 ml-1">
                  <Avatar
                    size={40}
                    src={avatarSrc || undefined}
                    className={`${avatarSrc ? '' : avatarColor} flex items-center justify-center !text-white font-medium text-sm shadow-sm transition-transform duration-300 group-hover:scale-105`}
                  >
                    {!avatarSrc && initials}
                  </Avatar>
                  {/* Overlapping badge */}
                  <div
                    className={`${rankColor.bg} ${rankColor.text} absolute -top-1.5 -left-1.5 w-[22px] h-[22px] rounded-full flex items-center justify-center text-[11px] font-black shadow-md shadow-black/10 border-2 ${isDark ? 'border-[#2d2d2d]' : 'border-white'} z-10`}
                  >
                    {rank}
                  </div>
                </div>

                {/* Name and Role */}
                <div className="flex-1 min-w-0 pr-4">
                  <div className="flex items-center gap-2 mb-0.5">
                    <Text
                      className={`text-sm font-semibold truncate ${isDark ? 'text-zinc-100 group-hover:text-white' : 'text-slate-700 group-hover:text-slate-900'
                        }`}
                    >
                      {fullName}
                    </Text>
                    {(() => {
                      const memberUserId = member.userId?.toString() || member.userId?._id?.toString() || member.userId;
                      const memberBadgeData = top5Badges[memberUserId];
                      const badgeIconUrl = memberBadgeData?.badge ? getBadgeIconUrl(memberBadgeData.badge) : null;
                      const hasStreak = memberBadgeData?.streak > 0;

                      return (
                        <>
                          {hasStreak && (
                            <Popover
                              content={renderStreakPopover(memberBadgeData.streak)}
                              trigger="hover"
                              placement="top"
                              mouseEnterDelay={0.1}
                            >
                              <div className="flex items-center gap-1 cursor-default">
                                {streakIconUrl ? (
                                  <img
                                    src={streakIconUrl}
                                    alt="Streak"
                                    className="w-4 h-4 object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <Flame size={14} className={isDark ? 'text-orange-400' : 'text-orange-500'} fill="currentColor" />
                                )}
                                <Text
                                  className={`text-xs font-medium ${isDark ? 'text-orange-400' : 'text-orange-600'
                                    }`}
                                >
                                  {memberBadgeData.streak}
                                </Text>
                              </div>
                            </Popover>
                          )}
                          {badgeIconUrl && (
                            <Popover
                              content={renderBadgePopover(memberBadgeData.badge)}
                              trigger="hover"
                              placement="top"
                              mouseEnterDelay={0.1}
                            >
                              <div className="w-4 h-4 rounded-full flex items-center justify-center overflow-hidden border border-amber-200 cursor-default">
                                <img
                                  src={badgeIconUrl}
                                  alt="Badge"
                                  className="w-full h-full object-cover rounded-full"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                            </Popover>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <Text
                    className={`text-xs truncate block ${isDark ? 'text-zinc-500' : 'text-slate-400'
                      }`}
                  >
                    {member.position || 'Team Member'}
                  </Text>
                </div>

                {/* Score */}
                <div className="flex-shrink-0 text-right pr-2">
                  <Text
                    className={`text-base font-bold tabular-nums block ${rank === 1 ? (isDark ? 'text-yellow-400' : 'text-yellow-600') :
                      isDark ? 'text-zinc-200' : 'text-slate-700'
                      }`}
                  >
                    {member.averageScore?.toFixed(1) || '0.0'}
                  </Text>
                </div>
              </motion.div>
            );
          })}

          {/* Separator if user is not in top 5 */}
          {showSeparator && (
            <div className="flex items-center justify-center py-2">
              <div className={`h-[1px] w-1/3 ${isDark ? 'bg-gradient-to-r from-transparent via-zinc-700 to-transparent' : 'bg-gradient-to-r from-transparent via-slate-300 to-transparent'}`}></div>
            </div>
          )}

          {/* Current User Entry if not in top 5 */}
          {currentUserEntry && (() => {
            const entryAvatar = resolveUserAvatar(currentUserEntry);
            return (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (top5.length + 1) * 0.08, duration: 0.3 }}
              className={`flex items-center gap-3 p-2 -mx-2 mt-1 rounded-lg border border-dashed ${isDark ? 'bg-zinc-800/30 border-zinc-700' : 'bg-slate-50/80 border-slate-200'
                }`}
            >
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <Avatar
                  size={38}
                  src={entryAvatar || undefined}
                  className={`${entryAvatar ? '' : `bg-gradient-to-tr ${isDark ? 'from-zinc-600 to-zinc-500' : 'from-slate-400 to-slate-300'}`} flex items-center justify-center !text-white font-medium text-sm`}
                >
                  {!entryAvatar && getInitials(currentUserEntry.firstName, currentUserEntry.lastName)}
                </Avatar>
              </div>

              {/* Name and Role */}
              <div className="flex-1 min-w-0 pr-4">
                <div className="flex items-center gap-2 mb-0.5">
                  <Text
                    className={`text-sm font-semibold truncate ${isDark ? 'text-zinc-100' : 'text-slate-700'
                      }`}
                  >
                    {`${currentUserEntry.firstName || ''} ${currentUserEntry.lastName || ''}`.trim()}
                  </Text>
                  {(() => {
                    const badgeIconUrl = userBadge ? getBadgeIconUrl(userBadge) : null;
                    const hasStreak = userStreak > 0;

                    return (
                      <>
                        {hasStreak && (
                          <Popover
                            content={renderStreakPopover(userStreak)}
                            trigger="hover"
                            placement="top"
                            mouseEnterDelay={0.1}
                          >
                            <div className="flex items-center gap-1 cursor-default">
                              {streakIconUrl ? (
                                <img
                                  src={streakIconUrl}
                                  alt="Streak"
                                  className="w-4 h-4 object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <Flame size={14} className={isDark ? 'text-orange-400' : 'text-orange-500'} fill="currentColor" />
                              )}
                              <Text
                                className={`text-xs font-medium ${isDark ? 'text-orange-400' : 'text-orange-600'
                                  }`}
                              >
                                {userStreak}
                              </Text>
                            </div>
                          </Popover>
                        )}
                        {badgeIconUrl && (
                          <Popover
                            content={renderBadgePopover(userBadge)}
                            trigger="hover"
                            placement="top"
                            mouseEnterDelay={0.1}
                          >
                            <div className="w-4 h-4 rounded-full flex items-center justify-center overflow-hidden border border-amber-200 cursor-default">
                              <img
                                src={badgeIconUrl}
                                alt="Badge"
                                className="w-full h-full object-cover rounded-full"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          </Popover>
                        )}
                      </>
                    );
                  })()}
                </div>
                <Text
                  className={`text-xs truncate block ${isDark ? 'text-zinc-500' : 'text-slate-400'
                    }`}
                >
                  {currentUserEntry.position || 'Team Member'}
                  <span className="ml-2 font-medium opacity-70">(You)</span>
                </Text>
              </div>

              {/* Score */}
              <div className="flex-shrink-0 text-right pr-2">
                <Text
                  className={`text-base font-bold tabular-nums block ${isDark ? 'text-white' : 'text-slate-900'
                    }`}
                >
                  {currentUserEntry.averageScore?.toFixed(1) || '0.0'}
                </Text>
              </div>
            </motion.div>
            );
          })()}
        </div>
      </Card>
    </motion.div>
  );
};

export default GoalsLeaderboardCard;
